import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

import ShoppingListDateStepV2 from './ListasV2/ShoppingListDateStepV2';
import ShoppingListReviewStepV2 from './ListasV2/ShoppingListReviewStepV2';
import ShoppingListFinalStepV2 from './ListasV2/ShoppingListFinalStepV2';
import ExperimentalPopupProvider from './ExperimentalPopups/ExperimentalPopupProvider';
import './ListasV2/ShoppingListCreatorV2.css';

const steps = ['Fechas', 'Revisión', 'Lista Final'];

// --- MAIN COMPONENT ---
function ShoppingListCreator({ preview = false }) {
  const { authToken } = useAuth();
  const navigate = useNavigate();

  // UI State
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Data State
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [userInfo, setUserInfo] = useState({ comensalesCasa: 1 });
  const [planningSummary, setPlanningSummary] = useState(preview ? { days: 7, meals: 12 } : null); // { days, meals }

  // Ingredient State (Step 2)
  // rawRecipesData removed
  const [ingredientsMap, setIngredientsMap] = useState({}); // { id: { ...data, quantity, unit } } - Base needed
  const [fridgeAmounts, setFridgeAmounts] = useState({}); // { id: amount }
  const [dinersState, setDinersState] = useState({}); // Renamed from dinersPerRecipe to fix weird RefError

  const [plannedRecipesList, setPlannedRecipesList] = useState([]); // Helper for modal

  // Final State (Step 3)
  const [currentListId, setCurrentListId] = useState(null); // ID if auto-saved
  const [savedStatus, setSavedStatus] = useState('idle'); // 'saving', 'saved', 'error'

  // 1. Initial Load: User Info
  useEffect(() => {
    if (preview) return;
    const fetchUser = async () => {
      try {
        const userRes = await fetch(`${import.meta.env.VITE_API_URL}/user`, { headers: { Authorization: `Bearer ${authToken}` } });
        const userData = await userRes.json();
        const uid = userData.id || userData._id;

        const infoRes = await fetch(`${import.meta.env.VITE_API_URL}/userinfo/user/${uid}`, { headers: { Authorization: `Bearer ${authToken}` } });
        if (infoRes.ok) {
          const infoData = await infoRes.json();
          setUserInfo(infoData);
        }
      } catch (err) { console.error("Error fetching user info", err); }
    };
    if (authToken) fetchUser();
  }, [authToken]);

  // 2. Step 1 Logic: Fetch Planning Summary when dates change
  useEffect(() => {
    if (preview) return;
    if (activeStep === 0) {
      const fetchPlanning = async () => {
        console.log("Fetching Planning Check:", { startDate, endDate });
        if (!startDate || !endDate) return; // Wait for full selection

        // Helper to get YYYY-MM-DD in local time
        const toLocalISO = (date) => {
          const offset = date.getTimezoneOffset() * 60000;
          return new Date(date - offset).toISOString().split('T')[0]; // Trick to keep local date part
        };
        // Or simpler:
        const formatDate = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const startStr = formatDate(startDate);
        const endStr = formatDate(endDate);
        try {
          const res = await fetch(`${import.meta.env.VITE_API_URL}/menuschema?start=${startStr}&end=${endStr}`, { headers: { Authorization: `Bearer ${authToken}` } });
          const data = await res.json();
          // Calc Summary
          const mealCount = data.reduce((acc, curr) => acc + (curr.recipes?.length || 0), 0);
          const diffTime = Math.abs(endDate - startDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
          setPlanningSummary({ days: diffDays, meals: mealCount });
        } catch (err) { console.error("Error fetching summary", err); }
      };
      fetchPlanning();
    }
  }, [startDate, endDate, activeStep, authToken]);

  // 3. Step 2 Logic: Consolidated Load & Recalc
  // A. Initial Load: Fetch Menu & Recipes, Initialize Diners
  const fetchMenuAndRecipes = React.useCallback(async () => {
    if (preview) {
      const mockIngredients = {
        i1: { name: 'Tomates cherry', quantity: 500, unit: 'gramos', subcategoria: 'Fruta y verdura', usedIn: ['Ensalada mediterránea'] },
        i2: { name: 'Espinacas frescas', quantity: 300, unit: 'gramos', subcategoria: 'Fruta y verdura', usedIn: ['Tortilla de espinacas'] },
        i3: { name: 'Pechuga de pollo', quantity: 800, unit: 'gramos', subcategoria: 'Carnicería', usedIn: ['Pollo al limón', 'Bowl de arroz'] },
        i4: { name: 'Yogur griego', quantity: 4, unit: 'unidades', gramsPerUnit: 125, subcategoria: 'Lácteos', usedIn: ['Desayunos'] },
        i5: { name: 'Arroz integral', quantity: 450, unit: 'gramos', subcategoria: 'Despensa', usedIn: ['Bowl de arroz'] },
      };
      const mockRecipes = [
        { key: 'r1', dateKey: '2026-07-15', date: '15/7/2026', meal: 'Comida', recipeId: 'r1', recipeName: 'Pollo al limón', servings: 2 },
        { key: 'r1-repeat', dateKey: '2026-07-17', date: '17/7/2026', meal: 'Cena', recipeId: 'r1', recipeName: 'Pollo al limón', servings: 2 },
        { key: 'r2', dateKey: '2026-07-16', date: '16/7/2026', meal: 'Desayuno', recipeId: 'r2', recipeName: 'Tortilla de espinacas', servings: 2 },
        { key: 'r3', dateKey: '2026-07-17', date: '17/7/2026', meal: 'Comida', recipeId: 'r3', recipeName: 'Bowl de arroz', servings: 2 },
      ];
      setDinersState({ r1: 2, 'r1-repeat': 2, r2: 2, r3: 2 });
      setPlannedRecipesList(mockRecipes);
      setIngredientsMap(mockIngredients);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const formatDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const startStr = formatDate(startDate);
      const endStr = formatDate(endDate);

      // Fetch Menu
      const menuRes = await fetch(`${import.meta.env.VITE_API_URL}/menuschema?start=${startStr}&end=${endStr}`, { headers: { Authorization: `Bearer ${authToken}` } });
      const menuData = await menuRes.json();

      // Build Init List & Defaults
      const recipeList = [];
      const recipeIdsToFetch = new Set();

      // We will perform a functional update to ensure we don't prefer stale state,
      // but we also need to build the recipe list.
      // Strategy: Calc defaults locally. Update state merging defaults.

      const defaults = {};

      menuData.forEach((dayMenu) => {
        const stockRecipeIds = new Set((dayMenu.stockUses || []).map(stockUse => {
          const recipeId = stockUse.recipe && typeof stockUse.recipe === 'object' ? stockUse.recipe._id : stockUse.recipe;
          const stockRecipeId = stockUse.stockItem && typeof stockUse.stockItem === 'object'
            ? (typeof stockUse.stockItem.sourceRecipe === 'object' ? stockUse.stockItem.sourceRecipe?._id : stockUse.stockItem.sourceRecipe)
            : null;
          return recipeId || stockRecipeId;
        }).filter(Boolean).map(id => id.toString()));

        dayMenu.recipes.forEach((rId, idx) => {
          if (!rId) return;
          const recipeId = typeof rId === 'object' ? rId._id : rId;
          if (stockRecipeIds.has(recipeId?.toString())) return;

          recipeIdsToFetch.add(recipeId);
          const key = `${dayMenu.date}-${dayMenu.meal}-${idx}-${recipeId}`;

          defaults[key] = userInfo.comensalesCasa || 1; // Default

          recipeList.push({ key, date: dayMenu.date, meal: dayMenu.meal, recipeId, menuId: dayMenu._id, servings: defaults[key] });
        });

        (dayMenu.stockPreparations || []).forEach((prep, idx) => {
          const recipeId = prep.recipe && typeof prep.recipe === 'object' ? prep.recipe._id : prep.recipe;
          if (!recipeId) return;

          recipeIdsToFetch.add(recipeId);
          const key = `${dayMenu.date}-${dayMenu.meal}-fondo-${idx}-${recipeId}`;
          defaults[key] = Number(prep.portions || 1);

          recipeList.push({
            key,
            date: dayMenu.date,
            meal: dayMenu.meal,
            recipeId,
            menuId: dayMenu._id,
            servings: defaults[key],
            isStockPrep: true,
            stockItem: prep.stockItem && typeof prep.stockItem === 'object' ? prep.stockItem._id : prep.stockItem,
            storage: prep.storage || 'freezer',
            createdFromPlan: `menu:${dayMenu._id}:recipe:${recipeId}:stock-prep`,
          });
        });
      });

      // Functional State Update:
      // Preserve existing edits (in prev) over defaults.
      // Only set key if missing in prev.
      setDinersState(prev => {
        const next = { ...defaults, ...prev };
        console.log("Setting Diners State. Next:", next);
        return next;
      });

      // Fetch Recipe Names/Details
      const recipesRes = await Promise.all(
        Array.from(recipeIdsToFetch).map(id => fetch(`${import.meta.env.VITE_API_URL}/recipes/${id}`).then(r => r.json()))
      );
      const recipeMap = recipesRes.reduce((acc, r) => ({ ...acc, [r._id]: r }), {});

      const enrichedList = recipeList.map(item => ({
        ...item,
        dateKey: item.date,
        date: new Date(item.date).toLocaleDateString(),
        recipeName: recipeMap[item.recipeId]?.name || 'Receta desconocida'
      }));

      setPlannedRecipesList(enrichedList);

    } catch (err) { console.error("Error fetching menu", err); }
    finally { setLoading(false); }
  }, [startDate, endDate, authToken, userInfo, preview]);

  // B. Calculate Ingredients: Depends on Planned List & Diners
  const calculateIngredients = React.useCallback(async () => {
    if (preview) return;
    if (plannedRecipesList.length === 0) return;
    // setLoading(true); // Don't block UI to keep modal open
    try {
      const consolidated = {};
      const allIngIds = new Set();
      const recipeUsageMap = {}; // { ingId: [recipeName] }

      for (const item of plannedRecipesList) {
        // Use current Diners state
        const dinersMap = dinersState;
        const fallbackServings = item.isStockPrep ? item.servings : (userInfo.comensalesCasa || 1);
        const servings = dinersMap[item.key] === undefined ? fallbackServings : dinersMap[item.key];

        if (servings <= 0) continue;

        const flattenRes = await fetch(`${import.meta.env.VITE_API_URL}/recipes/${item.recipeId}/flatten?servings=${servings}`);
        const flattenData = await flattenRes.json();

        Object.entries(flattenData.flattened || {}).forEach(([ingId, amount]) => {
          consolidated[ingId] = (consolidated[ingId] || 0) + Number(amount);
          allIngIds.add(ingId);

          // Track usage
          if (!recipeUsageMap[ingId]) recipeUsageMap[ingId] = new Set();
          recipeUsageMap[ingId].add(item.recipeName);
        });
      }

      const ingDetailsRes = await Promise.all(
        Array.from(allIngIds).map(id => fetch(`${import.meta.env.VITE_API_URL}/ingredients/${id}`).then(r => r.json()))
      );

      // Fallbacks de conversión para cucharas (coinciden con el backend en recipeComponents.js)
      const SPOON_GRAMS = { cucharadas: 15, cucharaditas: 5 };

      const finalMap = {};
      ingDetailsRes.forEach(ing => {
        const gramsTotal = consolidated[ing._id];
        const stdUnit = ing.standardUnit || 'gramos';
        let displayQty = gramsTotal;
        let unit = 'gramos';
        let gramsPerUnit = null; // gramos por unidad efectivos (solo si la unidad final no es gramos)

        // Reconvertimos los gramos a la unidad estándar del ingrediente cuando es posible
        if (stdUnit !== 'gramos') {
          const factor = (ing.gramsPerUnit && ing.gramsPerUnit > 0)
            ? ing.gramsPerUnit
            : SPOON_GRAMS[stdUnit];
          if (factor && factor > 0) {
            displayQty = gramsTotal / factor;
            unit = stdUnit;
            gramsPerUnit = factor;
          }
          // Si no hay forma de convertir (falta gramsPerUnit y no es cuchara), se queda en gramos
        }

        finalMap[ing._id] = {
          name: ing.name,
          quantity: Math.ceil(displayQty),
          unit: unit,
          gramsPerUnit: gramsPerUnit, // null cuando la unidad final es gramos
          subcategoria: ing.subcategoria || 'Despensa',
          usedIn: Array.from(recipeUsageMap[ing._id] || [])
        };
      });

      setIngredientsMap(finalMap);

    } catch (err) { console.error("Error calc ingredients", err); }
    finally { setLoading(false); }
  }, [plannedRecipesList, dinersState, userInfo, preview]); // Re-calc if List or Diners change

  // Effect: Trigger Calc when Diners update (if in Step 2)
  useEffect(() => {
    if (activeStep === 1) {
      calculateIngredients();
    }
  }, [dinersState, calculateIngredients, activeStep]);


  // Trigger Step 2 Load when entering step 2 OR when Diners change (debounce?)
  // Note: To avoid loops, we only hard-trigger on Step transition.
  // Manual "Refresh" inside step 2 logic handles diner updates.
  useEffect(() => {
    if (activeStep === 1) {
      fetchMenuAndRecipes();
    }
  }, [activeStep, fetchMenuAndRecipes]); // Reload if entering step 2. Diners update triggers re-calc inside Step 2 visually? No, requires re-flatten.

  // Re-calc ingredients if Diners change while in Step 2.
  // We add a specific effect hook for dinersPerRecipe, heavily debounced or check differentiation
  // Ideally, we move the load logic to a function called by the modal close.
  // refreshIngredientsDueToDinersChange removed


  // 4. Step 3 Logic: Auto Save
  const saveList = React.useCallback(async () => {
    setSavedStatus('saving');
    if (preview) {
      window.setTimeout(() => setSavedStatus('saved'), 450);
      return;
    }
    try {
      // Filter: Only what's needed (Needed - Fridge)
      const finalIngs = [];
      Object.entries(ingredientsMap).forEach(([id, data]) => {
        const fridge = Number(fridgeAmounts[id] || 0);
        const toBuy = data.quantity - fridge;
        if (toBuy > 0) {
          finalIngs.push({
            id: id,
            name: data.name,
            quantity: Math.round(toBuy * 100) / 100,
            unit: data.unit,
            gramsPerUnit: data.gramsPerUnit,
            subcategoria: data.subcategoria,
            notes: `Needed: ${data.quantity}, Have: ${fridge}`
          });
        }
      });

      const startStr = startDate.toLocaleDateString();
      const endStr = endDate.toLocaleDateString();
      const payload = {
        nombre: `Compra ${startStr} - ${endStr}`,
        ingredientes: finalIngs.map(i => ({
          idIngrediente: i.id, // Schema expects idIngrediente
          cantidad: i.quantity,
          unit: i.unit,
          gramsPerUnit: i.gramsPerUnit ?? undefined,
          estado: 'pendiente'
        })),
        // We might want to store extra meta like 'subcategoria' in the List model?
        // Currently List model only has [{idIngrediente, cantidad, estado}]
        // The display logic in Step 3 re-fetches names?
        // Or we store them? The current backend `POST` schema is:
        // { nombre, ingredientes: [{idIngrediente, cantidad, estado}] }
      };

      let method = 'POST';
      let url = `${import.meta.env.VITE_API_URL}/listascompra`;

      if (currentListId) {
        method = 'PUT';
        url = `${import.meta.env.VITE_API_URL}/listascompra/${currentListId}`;
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        const listId = data._id || currentListId;
        setCurrentListId(listId);

        const stockCreations = plannedRecipesList.map(item => {
          const plannedServings = Number(dinersState[item.key] ?? item.servings ?? userInfo.comensalesCasa ?? 1);
          const baseServings = Number(userInfo.comensalesCasa || 1);
          const surplus = item.isStockPrep ? plannedServings : plannedServings - baseServings;
          if (!Number.isFinite(surplus) || surplus <= 0) return null;

          return {
            sourceRecipe: item.recipeId,
            portionsAvailable: surplus,
            portionsTotal: surplus,
            storage: item.storage || 'freezer',
            createdFromPlan: item.createdFromPlan || `shopping:${listId}:${item.key}:surplus`,
            notes: item.isStockPrep
              ? `Fondo planificado desde ${item.meal}`
              : `Sobrantes automaticos desde lista de la compra (${plannedServings} raciones, base ${baseServings})`,
          };
        }).filter(Boolean);

        await Promise.all(stockCreations.map(body => fetch(`${import.meta.env.VITE_API_URL}/food-stock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify(body)
        })));

        setSavedStatus('saved');
      } else {
        setSavedStatus('error');
      }

    } catch (err) {
      console.error("Save error", err);
      setSavedStatus('error');
    }
  }, [ingredientsMap, fridgeAmounts, startDate, endDate, authToken, currentListId, plannedRecipesList, dinersState, userInfo, preview]);

  useEffect(() => {
    if (activeStep === 2 && savedStatus === 'idle') { // Auto save on entry, only when idle
      saveList();
    }
  }, [activeStep, savedStatus, saveList]);

  // --- Handlers ---
  const handleNext = () => {
    if (activeStep === 2) {
      navigate(preview ? '/preview/listas' : '/listas');
    } else {
      setActiveStep(p => p + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(p => p - 1);
    setSavedStatus('idle'); // Reset save status if going back to edit
  };

  // Calculate final display list for Step 3 (purely from state, no fetch needed)
  const getFinalDisplayList = () => {
    const list = {};
    Object.entries(ingredientsMap).forEach(([id, data]) => {
      const fridge = Number(fridgeAmounts[id] || 0);
      const toBuy = data.quantity - fridge;
      if (toBuy > 0) {
        list[id] = {
          ...data,
          quantity: Math.round(toBuy * 100) / 100,
          id: id
        };
      }
    });
    return list;
  };

  return (
    <ExperimentalPopupProvider>
      <main className="list-creator-v2">
        <header className="list-creator-v2-header"><div><span>Nueva lista</span><h1>Prepara tu compra</h1></div><div className="list-creator-v2-steps">{steps.map((label, index) => <div key={label} className={`${index === activeStep ? 'is-active' : ''} ${index < activeStep ? 'is-done' : ''}`}><i>{index < activeStep ? '✓' : index + 1}</i><span>{label}</span></div>)}</div></header>
        {activeStep === 0 && <ShoppingListDateStepV2 startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} summary={planningSummary} />}
        {activeStep === 1 && (loading ? <div className="lists-v2-empty">Calculando ingredientes…</div> : <ShoppingListReviewStepV2 ingredients={ingredientsMap} fridgeAmounts={fridgeAmounts} setFridgeAmounts={setFridgeAmounts} plannedRecipes={plannedRecipesList} servingsMap={dinersState} setServingsMap={setDinersState} globalDiners={userInfo.comensalesCasa || 1} onDinersUpdate={calculateIngredients} />)}
        {activeStep === 2 && <ShoppingListFinalStepV2 finalList={getFinalDisplayList()} onSave={saveList} savedStatus={savedStatus} />}
        <footer className="list-creator-v2-footer">{activeStep > 0 ? <Button onClick={handleBack} variant="text">← Volver</Button> : <span />}{activeStep === 0 && planningSummary?.meals === 0 && <small>Necesitas tener alguna comida planificada.</small>}<Button onClick={handleNext} disabled={activeStep === 0 && (!planningSummary || planningSummary.meals === 0)} variant="contained">{activeStep === 0 ? 'Revisar ingredientes' : activeStep === 1 ? 'Generar lista final' : 'Ver mis listas'}</Button></footer>
      </main>
    </ExperimentalPopupProvider>
  );
}

export default ShoppingListCreator;
